import { Component, Input, ViewChild, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { AcceptanceCriterion, TemplateGroupInfo } from '../../../../shared/templateGen/templateInterfaces';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StatoSessioneService } from '../../../../shared/services/stato-sessione.service';
import { ApiService } from '../../../../shared/services/api.service';
import { SpinnerService } from '../../../../shared/services/spinner.service';
import Swal from 'sweetalert2';
import {AuthService} from '../../../../shared/auth/auth.service';
import {RigeneraTestModalComponent} from '../../elements/modals/rigenera-test-modal/rigenera-test-modal.component';
import { MicroScenarioUIData } from '../../test/test-table/test-table.component';
import {GherkinSteps, MacroScenarioItem} from '../three-column-workspace/three-column-workspace.component';

interface Template {
  templateName: string;
  columns: string[];
  templateDescription: string;
}

@Component({
  selector: 'app-criterion',
  templateUrl: './criterion.component.html',
  styleUrls: ['./criterion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CriterionComponent {
  @Input() criterion?: AcceptanceCriterion;
  @Input() microScenario?: MicroScenarioUIData;
  @Input() macroScenarioData?: MacroScenarioItem;
  @Input() displayMode: 'criterion' | 'microScenarioSummary' | 'macroHeader' = 'criterion';

  @Input() readonly groupInfo?: TemplateGroupInfo;
  @Input() readonly compact = false;
  @Input() readonly groupIndex?: number;
  @Input() readonly criterionIndex?: number;

  @ViewChild('editModal') editModal: any;
  @ViewChild('testDataEditModal') testDataEditModal: any;

  editingCriterion: AcceptanceCriterion | null = null;

  isEditingTestData = false;
  editingTestData = {
    fieldName: '',
    value: '',
    index: -1,
    currentCriterion: null as AcceptanceCriterion | null
  };
  availableDataParamFields: string[] = [];
  selectedDataParamField = '';

  readonly projectLanguage$ = this.sessioneService.projectLanguage$;
  readonly templates$ = this.sessioneService.templates$;

  constructor(
    private modalService: NgbModal,
    private sessioneService: StatoSessioneService,
    private cd: ChangeDetectorRef
  ) {}

  get scenarioId(): string | undefined {
    if (this.displayMode === 'criterion') {
      return this.criterion?.id;
    } else if (this.displayMode === 'microScenarioSummary') {
      return this.microScenario?.id;
    } else if (this.displayMode === 'macroHeader') {
      return this.macroScenarioData?.id;
    }
    return undefined;
  }

  get scenarioName(): string | undefined {
    if (this.displayMode === 'criterion') {
      return this.criterion?.name;
    } else if (this.displayMode === 'microScenarioSummary') {
      return this.microScenario?.specification;
    } else if (this.displayMode === 'macroHeader') {
      return this.macroScenarioData?.scenario_title;
    }
    return undefined;
  }

  get scenarioTags(): string[] | undefined {
    if (this.displayMode === 'criterion') {
      return this.criterion?.tags;
    } else if (this.displayMode === 'microScenarioSummary') {
      return this.microScenario?.tags;
    } else if (this.displayMode === 'macroHeader') {
      return this.macroScenarioData?.tags;
    }
    return undefined;
  }

  get scenarioRef(): string | undefined {
    if (this.displayMode === 'criterion') {
      return this.criterion?.scenario_ref;
    }
    return undefined;
  }

  get macroNarrativeDescription(): string | undefined {
    if (this.displayMode === 'macroHeader' && this.macroScenarioData) {
      return this.macroScenarioData.narrative_description;
    }
    return undefined;
  }

  get macroGherkinSteps(): GherkinSteps | undefined {
    if (this.displayMode === 'macroHeader' && this.macroScenarioData) {
      return this.macroScenarioData.steps;
    }
    return undefined;
  }

  openEditModal(modal: any) {
    if (this.displayMode !== 'criterion' || !this.criterion) {
      return;
    }
    this.editingCriterion = JSON.parse(JSON.stringify(this.criterion));
    this.editingCriterion.tags = this.editingCriterion.tags || [];
    this.editingCriterion.given = this.editingCriterion.given || [];
    this.editingCriterion.when = this.editingCriterion.when || [];
    this.editingCriterion.then = this.editingCriterion.then || [];
    this.editingCriterion.test_data_elements = this.editingCriterion.test_data_elements || [];
    this.modalService.open(modal, { size: 'lg' });
  }

  saveChanges(modal: any) {
    if (this.displayMode !== 'criterion' || !this.editingCriterion || typeof this.groupIndex === 'undefined' || typeof this.criterionIndex === 'undefined') {
        return;
    }
    if (!this.validateCriterion(this.editingCriterion)) {
      return;
    }
    const cleanedCriterion = this.cleanCriterion(this.editingCriterion);
    this.sessioneService.updateCriterion(
      this.groupIndex,
      this.criterionIndex,
      cleanedCriterion
    );
    if (this.criterion) {
        this.criterion = cleanedCriterion;
    }
    this.cd.markForCheck();
    modal.close();
    Swal.fire({ title: 'Success', text: 'Changes saved successfully', icon: 'success', timer: 1500 });
  }

  addCondition(array: string[]) {
    if (this.displayMode !== 'criterion') return;
    if (array) {
      array.push('');
    }
  }

  removeCondition(array: string[], index: number) {
    if (this.displayMode !== 'criterion') return;
    if (array) {
      array.splice(index, 1);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }

  removeTag(criterionObj: AcceptanceCriterion | MicroScenarioUIData | MacroScenarioItem, index: number): void {
    if (this.displayMode !== 'criterion' || !this.criterion || typeof this.groupIndex === 'undefined' || typeof this.criterionIndex === 'undefined') {
        console.warn("Tag removal only available in 'criterion' mode with criterion data.");
        return;
    }
    const currentCriterion = criterionObj as AcceptanceCriterion;
    Swal.fire({
      title: 'Remove Tag', text: 'Are you sure you want to remove this tag?', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, remove it'
    }).then((result) => {
      if (result.isConfirmed) {
        if (currentCriterion.tags) {
          currentCriterion.tags.splice(index, 1);
          this.sessioneService.updateCriterion(this.groupIndex!, this.criterionIndex!, currentCriterion);
          this.cd.markForCheck();
        }
      }
    });
  }

  openAddTagModal(criterionObj: AcceptanceCriterion | MicroScenarioUIData | MacroScenarioItem): void {
     if (this.displayMode !== 'criterion' || !this.criterion || typeof this.groupIndex === 'undefined' || typeof this.criterionIndex === 'undefined') {
        console.warn("Adding tags only available in 'criterion' mode with criterion data.");
        return;
    }
    const currentCriterion = criterionObj as AcceptanceCriterion;
    if (!currentCriterion.tags) {
      currentCriterion.tags = [];
    }
    const availableTags = ['#business_critical','#security','#api_integration','#ui','#performance','#stateful','#validation','#async_concurrency','#history_of_bugs'];
    const unusedTags = availableTags.filter(tag => !currentCriterion.tags?.includes(tag));
    if (unusedTags.length === 0) {
      Swal.fire({title: 'All Tags Used', text: 'You have already used all available tags for this criterion.', icon: 'info'});
      return;
    }
    Swal.fire({
      title: 'Add Tag', input: 'select', inputOptions: unusedTags.reduce((options: {[key: string]: string}, tag) => { options[tag] = tag.replace(/_/g, ' '); return options; }, {}),
      inputPlaceholder: 'Select a tag', showCancelButton: true, confirmButtonText: 'Add',
      inputValidator: (value) => !value ? 'You need to select a tag!' : null
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        currentCriterion.tags!.push(result.value);
        this.sessioneService.updateCriterion(this.groupIndex!, this.criterionIndex!, currentCriterion);
        this.cd.markForCheck();
      }
    });
  }

  openEditTestDataModal(index: number): void {
    if (this.displayMode !== 'criterion' || !this.criterion) return;
    if (!this.criterion.test_data_elements) this.criterion.test_data_elements = [];
    const dataElement = this.criterion.test_data_elements[index];
    if (!dataElement) return;
    const parts = dataElement.split('|');
    this.editingTestData = {fieldName: parts[0].trim(), value: parts.length > 1 ? parts.slice(1).join('|').trim() : '', index: index, currentCriterion: this.criterion };
    this.modalService.open(this.testDataEditModal, { size: 'md' });
  }

  openAddTestDataModal(): void {
    if (this.displayMode !== 'criterion' || !this.criterion) return;
    if (!this.criterion.test_data_elements) this.criterion.test_data_elements = [];
    this.availableDataParamFields = [];
    const dataParams = this.sessioneService.getDataParameters();
    if (dataParams?.analysis_results?.test_data_management?.base_dataset) {
      this.availableDataParamFields = dataParams.analysis_results.test_data_management.base_dataset.map(dataset => dataset.field);
    }
    this.editingTestData = { fieldName: '', value: '', index: -1, currentCriterion: this.criterion };
    this.selectedDataParamField = '';
    this.modalService.open(this.testDataEditModal, { size: 'md' });
  }

  onDataParamFieldSelected(): void {
    if (this.displayMode !== 'criterion') return;
    if (!this.selectedDataParamField) return;
    this.editingTestData.fieldName = this.selectedDataParamField;
    const dataParams = this.sessioneService.getDataParameters();
    if (dataParams?.analysis_results?.test_data_management?.base_dataset) {
      const selectedDataset = dataParams.analysis_results.test_data_management.base_dataset.find(dataset => dataset.field === this.selectedDataParamField);
      if (selectedDataset?.valid_values?.length) {
        this.editingTestData.value = "Valid values: " + selectedDataset.valid_values.join(', ');
      }
    }
  }

  saveTestDataChanges(modal: any): void {
    if (this.displayMode !== 'criterion' || typeof this.groupIndex === 'undefined' || typeof this.criterionIndex === 'undefined') return;
    const { fieldName, value, index, currentCriterion } = this.editingTestData;
    if (!fieldName.trim()) { Swal.fire({ title: 'Error', text: 'Field name is required', icon: 'error' }); return; }
    if (!currentCriterion) return;
    if (!currentCriterion.test_data_elements) currentCriterion.test_data_elements = [];
    const formattedElement = `${fieldName.trim()} | ${value.trim()}`;
    if (index >= 0) { currentCriterion.test_data_elements[index] = formattedElement; } else { currentCriterion.test_data_elements.push(formattedElement); }
    this.sessioneService.updateCriterion(this.groupIndex, this.criterionIndex, currentCriterion );
    this.cd.markForCheck();
    modal.close();
  }

  removeTestDataElement(index: number): void {
    if (this.displayMode !== 'criterion' || !this.criterion || typeof this.groupIndex === 'undefined' || typeof this.criterionIndex === 'undefined') return;
    Swal.fire({
      title: 'Remove Test Data', text: 'Are you sure you want to remove this test data element?', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Yes, remove it'
    }).then((result) => {
      if (result.isConfirmed) {
        if (this.criterion!.test_data_elements) {
          this.criterion!.test_data_elements.splice(index, 1);
          this.sessioneService.updateCriterion(this.groupIndex!, this.criterionIndex!, this.criterion!);
          this.cd.markForCheck();
        }
      }
    });
  }

  getFieldName(dataElement: string): string {
    if (!dataElement) return '';
    const parts = dataElement.split('|');
    return parts[0].trim();
  }

  getTooltipValue(dataElement: string): string {
    if (!dataElement) return '';
    const parts = dataElement.split('|');
    if (parts.length < 2) return '';
    return parts.slice(1).join('|').trim();
  }

  private validateCriterion(criterionToValidate: AcceptanceCriterion): boolean {
    if (this.displayMode !== 'criterion') return true;
    if (!criterionToValidate?.name?.trim()) { Swal.fire({ title: 'Error', text: 'Scenario name is required', icon: 'error' }); return false; }
    if (!criterionToValidate.when?.[0]?.trim() || !criterionToValidate.then?.[0]?.trim()) { Swal.fire({ title: 'Error', text: 'At least one When action and Then result is required', icon: 'error' }); return false; }
    return true;
  }

  private cleanCriterion(criterionToClean: AcceptanceCriterion): AcceptanceCriterion {
    return {
      ...criterionToClean, id: criterionToClean.id, name: criterionToClean.name.trim(), scenario_ref: criterionToClean.scenario_ref,
      tags: (criterionToClean.tags || []).filter(t => t?.trim()),
      given: (criterionToClean.given || []).filter(g => g?.trim()),
      when: (criterionToClean.when || []).filter(w => w?.trim()),
      then: (criterionToClean.then || []).filter(t => t?.trim()),
      test_data_elements: criterionToClean.test_data_elements || []
    };
  }

  regenerateTests(): void {
    if (this.displayMode !== 'criterion' || !this.criterion) {
      console.warn("Test regeneration only available in 'criterion' mode with criterion data.");
      return;
    }
    const modalRef = this.modalService.open(RigeneraTestModalComponent, { size: 'xl' });
    modalRef.componentInstance.criterion = this.criterion;
    modalRef.componentInstance.groupInfo = this.groupInfo;
     if (typeof this.groupIndex !== 'undefined' && typeof this.criterionIndex !== 'undefined') {
      modalRef.componentInstance.groupIndex = this.groupIndex;
      modalRef.componentInstance.criterionIndex = this.criterionIndex;
    }
    modalRef.result.then((result) => { console.log('Regenerate tests modal closed with:', result); this.cd.markForCheck(); })
                    .catch((reason) => { console.log('Regenerate tests modal dismissed:', reason); });
  }

  protected getContextFromDataParameters(fieldName: string): string {
    if (this.displayMode !== 'criterion') return '';
    const dataParams = this.sessioneService.getDataParameters();
    if (!dataParams?.analysis_results?.test_data_management?.base_dataset) return '';
    const matchingDataset = dataParams.analysis_results.test_data_management.base_dataset.find(dataset => dataset.field.toLowerCase() === fieldName.toLowerCase());
    return matchingDataset?.rules || '';
  }
}
